import { useEffect, useMemo, useState } from 'react'
import { listUsers } from 'account/components/userManagement/users/new-actions'
import { usersSelector } from 'account/components/userManagement/users/selectors'
import ApiClient from 'api-client/ApiClient'
import { useAppSelector } from 'app/store'
import withProgress from 'core/components/progress/withProgress'
import Dropdown from 'core/elements/dropdown'
import GlobalDropdownWithSearch from 'core/elements/dropdown/GlobalDropdownWithSearch'
import useListAction from 'core/hooks/useListAction'
import { getCurrentUser } from 'core/utils/rbac'

const AsyncDropdown = withProgress(Dropdown, { inline: true })

interface TenantUsersPicklistProps {
  tenantId: string
  disabled?: boolean
  name?: string
  label?: string
  onChange: (value: string) => void
  value: string
  width?: number
  align?: 'left' | 'right'
  [key: string]: unknown
}

// FR-001: Filter users by tenant role assignments
// FR-002: Exclude users without tenant roles from dropdown
// FR-004: Exclude readonly users (role=reader) from dropdown
// Reader role seems to also inherit load-balancer_observer
const READONLY_ROLES = ['reader', 'load-balancer_observer']
export default function TenantUsersPicklist({
  tenantId,
  disabled = false,
  name = 'user',
  label = 'User',
  onChange,
  value,
  width = 180,
  align = 'left',
  ...rest
}: TenantUsersPicklistProps) {
  const { loading: usersLoading } = useListAction(listUsers)
  const allUsers = useAppSelector(usersSelector)

  const [tenantUserIds, setTenantUserIds] = useState<Set<string>>(new Set())
  // FR-003: Show loading state while fetching role assignments
  const [loadingRoles, setLoadingRoles] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!tenantId) return undefined

    let cancelled = false
    setLoadingRoles(true)
    setError(null)

    const { keystone } = ApiClient.getInstance()
    keystone
      .getTenantRoleAssignments(tenantId, true)
      .then((assignments) => {
        if (cancelled) return
        // FR-004: Exclude users who only have readonly roles in this tenant
        const nonReadonlyUserIds = new Set<string>()
        assignments?.forEach((a) => {
          if (!a?.user?.id) return
          const roleName = a?.role?.name?.toLowerCase()
          if (!READONLY_ROLES.includes(roleName)) {
            nonReadonlyUserIds.add(a.user.id)
          }
        })
        setTenantUserIds(nonReadonlyUserIds)
      })
      .catch(() => {
        if (cancelled) return
        setError('Failed to load tenant users')
        setTenantUserIds(new Set())
      })
      .finally(() => {
        if (!cancelled) setLoadingRoles(false)
      })

    return () => {
      cancelled = true
    }
  }, [tenantId])

  const items = useMemo(() => {
    // if (!tenantUserIds.size) return []
    const currentUser = getCurrentUser()
    return (
      allUsers
        // Filter down to tenant users with CRUD access
        // Also add in current user if not on the list, and
        // add in all SSO users as a workaround for the
        // limitations of the current role assignments API
        .filter(
          (user) => tenantUserIds.has(user.id) || !user.isLocal || user.id === currentUser?.id,
        )
        .map((user) => ({
          label: user.username || user.displayname,
          value: user.id,
        }))
        .sort((a, b) => a.label.localeCompare(b.label))
    )
  }, [allUsers, tenantUserIds])

  return (
    <GlobalDropdownWithSearch
      {...rest}
      label={label}
      items={items}
      loading={loadingRoles || usersLoading}
      onChange={(item) => onChange(item.value)}
      value={value}
      showLabelOnTop
      align={align}
      disabled={disabled}
      width={width}
    />
  )
}
